import { ErrorHandler, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BusinessFinderService } from './business-finder.service';
import { UniversityResult } from '../models/university-result.model';
import { LatLon } from '../../util/geodesy/latlon';
import { BoundingBox } from '../../util/geodesy/bounding-box';

/**
 * HTTP service for the University Finder. Calls the AirportsAPI `~/universities` endpoint. Category facet
 * filtering (Degrees / Majors / Accreditation / ...) is applied client-side in {@link SearchPageComponent};
 * this service performs only the location/state/keyword query.
 */
@Injectable({
  providedIn: 'root'
})
export class UniversitiesService extends BusinessFinderService<UniversityResult> {

  protected readonly endpoint = 'universities';

  constructor(http: HttpClient, errorHandler: ErrorHandler) {
    super(http, errorHandler);
  }

  protected parse(data: any): UniversityResult {
    return UniversityResult.parse(data);
  }

  getUniversitiesByLatLon(latlon: LatLon, radius: number): Promise<UniversityResult[]> {
    return this.getByLatLon(latlon, radius);
  }

  getUniversitiesByBoundingBox(bounds: BoundingBox): Promise<UniversityResult[]> {
    return this.getByBoundingBox(bounds);
  }

  getUniversitiesByState(state: string): Promise<UniversityResult[]> {
    return this.getByState(state);
  }

  getUniversitiesBySearchTerm(term: string): Promise<UniversityResult[]> {
    return this.getBySearchTerm(term);
  }

  /**
   * Returns every university nationwide. Used when no location/keyword was entered — unlike the other
   * finders, there are few enough universities (~300) that an unscoped search returns the whole dataset
   * instead of requiring a location.
   */
  async getAllUniversities(): Promise<UniversityResult[]> {
    try {
      const response = await this._http.post<any[]>(this.url, {}).toPromise();
      return response.map(d => this.parse(d));
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }
}
